import React, { useMemo } from 'react'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
	type Control,
	type Path,
	type FieldValues,
	Controller,
	useFormContext,
} from 'react-hook-form'
import dayjs, { type Dayjs } from 'dayjs'
import useFormField from '../../hooks/useFormField'

export type DatePickerProps<T extends FieldValues = FieldValues> = {
	variant?: 'standard' | 'outlined' | 'filled'
	label?: string
	control?: Control<T, unknown>
	required?: boolean
	name?: Path<T>
	errors?: Record<string, unknown>
	clearable?: boolean
	minDate?: Date | null
	maxDate?: Date | null
	value?: Date | null
	onChange?: (date: Date | null) => void
	disabled?: boolean
	error?: string
	helperText?: string
	[key: string]: unknown
}

const DatePicker = <T extends FieldValues = FieldValues>({
	variant = 'standard',
	label,
	required,
	name,
	errors: propsErrors,
	control: propsControl,
	clearable = false,
	minDate,
	maxDate,
	value,
	onChange,
	disabled = false,
	error,
	helperText,
	...rest
}: DatePickerProps<T>) => {
	const { getError } = useFormField()
	const formContext = useFormContext<T>()

	const control = propsControl || formContext?.control
	const errors = propsErrors || formContext?.formState.errors

	const fieldError =
		error ?? (name && errors ? getError(name, errors) : undefined)

	const labelTxt = useMemo(
		() => (required && label ? `${label} *` : label),
		[required, label]
	)
	const slotProps = useMemo(
		() => ({
			textField: {
				variant,
				error: !!fieldError,
				helperText: fieldError
					? (fieldError.message ?? fieldError)
					: helperText,
				disabled,
			},
			field: { clearable },
		}),
		[variant, fieldError, clearable, helperText, disabled]
	)

	const toDayjs = (
		date: Date | null | undefined
	): Dayjs | undefined => (date ? dayjs(date) : undefined)

	const fromDayjs = (date: Dayjs | null | undefined): Date | null =>
		date ? date.toDate() : null

	if (control && name) {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Controller
					name={name}
					control={control}
					render={({ field }) => (
						<MuiDatePicker
							label={labelTxt}
							value={toDayjs(field.value)}
							onChange={(date) => field.onChange(fromDayjs(date))}
							slotProps={slotProps}
							minDate={toDayjs(minDate)}
							maxDate={toDayjs(maxDate)}
							disabled={disabled}
							{...rest}
						/>
					)}
				/>
			</LocalizationProvider>
		)
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<MuiDatePicker
				label={labelTxt}
				value={toDayjs(value)}
				onChange={(date) => onChange && onChange(fromDayjs(date))}
				slotProps={slotProps}
				minDate={toDayjs(minDate)}
				maxDate={toDayjs(maxDate)}
				disabled={disabled}
				{...rest}
			/>
		</LocalizationProvider>
	)
}

export default DatePicker
